import React, { useState, useEffect } from "react";
import { Card, Row, Col, Statistic, Table, Spin, Empty, Select, Tag, Button, message } from "antd";
import {
  DollarOutlined,
  TeamOutlined,
  RiseOutlined,
  FallOutlined,
  TrophyOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 8px 0; }
  .sub-heading-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  p { color: #666; margin: 0; font-size: 14px; }
`;

const InsightsWrapper = styled.div`
  min-height: 100vh;

  .stats-row {
    margin-bottom: 24px;
  }

  .stat-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    height: 100%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .stat-icon {
      font-size: 24px;
      margin-bottom: 12px;
    }

    .ant-statistic-title {
      color: #666;
      font-size: 13px;
    }

    .ant-statistic-content-value {
      color: #014c75;
      font-weight: 600;
    }
  }

  .chart-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      .chart-title {
        font-size: 16px;
        font-weight: 600;
        color: #014c75;
      }
    }
  }

  .table-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .table-title {
      font-size: 16px;
      font-weight: 600;
      color: #014c75;
      margin-bottom: 16px;
    }
  }
`;

const CHART_COLORS = ["#014c75", "#00d9a9", "#faad14", "#1890ff", "#722ed1", "#eb2f96", "#52c41a", "#13c2c2"];

const formatCurrency = (value) => {
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)}Cr`;
  } else if (value >= 100000) {
    return `₹${(value / 100000).toFixed(2)}L`;
  } else if (value >= 1000) {
    return `₹${(value / 1000).toFixed(1)}K`;
  }
  return `₹${value?.toLocaleString() || 0}`;
};

const FinancialInsights = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [metrics, setMetrics] = useState({
    totalEarnings: 0,
    totalTalentsHired: 0,
    benchPoolSize: 0,
    avgEarningPerTalent: 0,
    rejectedApplications: 0,
    activeJobs: 0,
  });
  const [earningsByMonth, setEarningsByMonth] = useState([]);
  const [roleDistribution, setRoleDistribution] = useState([]);
  const [topRolesHired, setTopRolesHired] = useState([]);
  const [rejectionAnalysis, setRejectionAnalysis] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  useEffect(() => {
    fetchFinancialInsights().catch(() => {});
  }, [selectedYear]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await fetchFinancialInsights();
      message.success("Financial data synced successfully");
    } catch (error) {
      message.error("Failed to sync financial data");
    } finally {
      setSyncing(false);
    }
  };

  const fetchFinancialInsights = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.BASE_URL}/partner-financial-insights?year=${selectedYear}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        const data = response.data.data;
        setMetrics({
          totalEarnings: data.totalEarnings || 0,
          totalTalentsHired: data.totalTalentsHired || 0,
          benchPoolSize: data.benchPoolSize || 0,
          avgEarningPerTalent: data.avgEarningPerTalent || 0,
          rejectedApplications: data.rejectedApplications || 0,
          activeJobs: data.activeJobs || 0,
        });
        setEarningsByMonth(data.earningsByMonth || []);
        setRoleDistribution(data.roleDistribution || []);
        setTopRolesHired(data.topRolesHired || []);
        setRejectionAnalysis(data.rejectionAnalysis || []);
      }
    } catch (error) {
      console.error("Error fetching financial insights:", error);
      setMetrics({
        totalEarnings: 0,
        totalTalentsHired: 0,
        benchPoolSize: 0,
        avgEarningPerTalent: 0,
        rejectedApplications: 0,
        activeJobs: 0,
      });
      setEarningsByMonth([]);
      setRoleDistribution([]);
      setTopRolesHired([]);
      setRejectionAnalysis([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const topRolesColumns = [
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Talents Hired",
      dataIndex: "count",
      key: "count",
      align: "center",
    },
    {
      title: "Total Earnings",
      dataIndex: "totalEarnings",
      key: "totalEarnings",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      title: "Avg Earning",
      dataIndex: "avgEarning",
      key: "avgEarning",
      align: "right",
      render: (value) => formatCurrency(value),
    },
  ];

  const rejectionColumns = [
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      align: "center",
      width: 80,
    },
    {
      title: "Percentage",
      dataIndex: "percentage",
      key: "percentage",
      align: "right",
      width: 100,
      render: (value) => `${value}%`,
    },
  ];

  if (loading) {
    return (
      <InsightsWrapper>
        <div style={{ textAlign: "center", padding: "100px 0" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "#666" }}>Loading Financial Insights...</p>
        </div>
      </InsightsWrapper>
    );
  }

  return (
    <InsightsWrapper>
      <PageHeader>
        <h1>Financial Insights</h1>
        <div className="sub-heading-row">
          <p>Track your earnings, talent performance, and hiring trends analysis</p>
          <Button
            icon={<SyncOutlined spin={syncing} />}
            onClick={handleSync}
            loading={syncing}
          >
            Sync Now
          </Button>
        </div>
      </PageHeader>

      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card" bordered={false}>
            <DollarOutlined className="stat-icon" style={{ color: "#014c75" }} />
            <Statistic
              title="Total Earnings"
              value={metrics.totalEarnings}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card" bordered={false}>
            <TeamOutlined className="stat-icon" style={{ color: "#00d9a9" }} />
            <Statistic title="Talents Hired" value={metrics.totalTalentsHired} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card" bordered={false}>
            <TrophyOutlined className="stat-icon" style={{ color: "#1890ff" }} />
            <Statistic title="Bench Pool" value={metrics.benchPoolSize} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card" bordered={false}>
            <RiseOutlined className="stat-icon" style={{ color: "#faad14" }} />
            <Statistic title="Active Applications" value={metrics.activeJobs} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card" bordered={false}>
            <DollarOutlined className="stat-icon" style={{ color: "#722ed1" }} />
            <Statistic
              title="Avg/Talent"
              value={metrics.avgEarningPerTalent}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card" bordered={false}>
            <CloseCircleOutlined className="stat-icon" style={{ color: "#f5222d" }} />
            <Statistic title="Rejections" value={metrics.rejectedApplications} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <div className="chart-card">
            <div className="chart-header">
              <span className="chart-title">Monthly Earnings Analysis</span>
              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                style={{ width: 100 }}
                options={yearOptions.map((year) => ({ label: year, value: year }))}
              />
            </div>
            {earningsByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={earningsByMonth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => formatCurrency(value)} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="earnings" name="Earnings" fill="#00d9a9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No earnings data available" />
            )}
          </div>
        </Col>

        <Col xs={24} lg={10}>
          <div className="chart-card">
            <div className="chart-header">
              <span className="chart-title">Earnings by Role</span>
            </div>
            {roleDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={roleDistribution}
                    cx="50%"
                    cy="45%"
                    innerRadius={70}
                    outerRadius={120}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="role"
                  >
                    {roleDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    formatter={(value) => <span style={{ color: "#333" }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No role distribution data" />
            )}
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <div className="table-card" style={{ height: "100%" }}>
            <div className="table-title">Top Roles Hired</div>
            <Table
              columns={topRolesColumns}
              dataSource={topRolesHired}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
              size="small"
              rowKey="role"
              locale={{ emptyText: <Empty description="No data available" /> }}
            />
          </div>
        </Col>

        <Col xs={24} lg={12}>
          <div className="table-card" style={{ height: "100%" }}>
            <div className="table-title">Rejection Analysis</div>
            <Table
              columns={rejectionColumns}
              dataSource={rejectionAnalysis}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
              size="small"
              rowKey="reason"
              locale={{ emptyText: <Empty description="No rejections to analyze" /> }}
            />
          </div>
        </Col>
      </Row>
    </InsightsWrapper>
  );
};

export default FinancialInsights;
